import { Card } from '@tt/design-system';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import type { PaymentConfirmPayload, PaymentMethod } from '../../types/payment';
import { CardMethod } from './CardMethod';
import { CryptoMethod } from './CryptoMethod';
import { MilesMethod } from './MilesMethod';
import { PaymentMethodTabs } from './PaymentMethodTabs';
import { PixMethod } from './PixMethod';
import { PointsMethod } from './PointsMethod';

type PaymentMethodSelectorProps = {
  registrationId: string;
  amount: number;
  onConfirm: (payload: PaymentConfirmPayload) => void;
  isConfirming?: boolean;
};

/**
 * Orquestrador: tabs no topo + bloco do método ativo abaixo com cross-fade.
 *
 * Render condicional (não keep-alive) — apenas o método ativo é montado.
 * AnimatePresence garante mount/unmount limpo. Timer do PIX reseta ao trocar
 * de aba e voltar; comportamento aceito.
 */
export function PaymentMethodSelector({
  registrationId,
  amount,
  onConfirm,
  isConfirming,
}: PaymentMethodSelectorProps) {
  const [active, setActive] = useState<PaymentMethod>('pix');
  const reduced = usePrefersReducedMotion();

  const methodProps = { registrationId, amount, onConfirm, isConfirming };

  return (
    <Card tone="raised" padding="lg" className="flex flex-col gap-6">
      <PaymentMethodTabs value={active} onChange={setActive} />

      <div
        role="tabpanel"
        id={`pm-panel-${active}`}
        aria-labelledby={`pm-tab-${active}`}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={active}
            initial={reduced ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, y: -4 }}
            transition={{ duration: reduced ? 0.1 : 0.22, ease: 'easeOut' }}
          >
            {active === 'pix' && <PixMethod {...methodProps} />}
            {active === 'card' && <CardMethod {...methodProps} />}
            {active === 'points' && <PointsMethod {...methodProps} />}
            {active === 'miles' && <MilesMethod {...methodProps} />}
            {active === 'crypto' && <CryptoMethod {...methodProps} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </Card>
  );
}
